/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import { PeriodPicker } from '../Inputs/PeriodPicker';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { Section } from './Section';
import { DaysPicker } from '../Inputs/DaysPicker';
import { Input } from '../Inputs/Input';
import { SectorPicker } from '../Inputs/SectorPicker';
import { Advertiser, Agency, Country, Currency, DiscountRate, MediaChannel, Product, Sector, Target } from '../../Business/dto/Brief';
import { BriefJAMDTO } from '../../Business/dto/BriefJAM';
import { TargetPicker } from '../Inputs/TargetPicker';
import './GeneralSection.css';
import { MediaChannelPicker } from '../Inputs/MediaChannelPicker';
import { useAppDispatch, useAppSelector } from '../../Redux/hooks';
import { RetrieveDataFromExternalSource, setInputValue } from '../../Redux/Reducers/briefSlice';
import { FormatPicker } from '../Inputs/FormatPicker';
import { CountryPicker } from '../Inputs/CountryPicker';
import { AgencyPicker } from '../Inputs/AgencyPicker';
import { AdvertiserPicker } from '../Inputs/AdvertiserPicker';
import { ProductPicker } from '../Inputs/ProductPicker';
import { DataKey } from '../../Business/dto/ExternalSystemElement';
import { useTranslation } from 'react-i18next';
import { SellingStrategyOption } from '../Inputs/SellingStrategy/SellingStrategyPicker';
import { ExternalSystemElementApiClient } from '../../Business/api/ExternalSystemElementApiClient';
import { ApiClientManager } from '../../Business/api/ApiClientManager';
import { dateObjectToNumber } from '../../Business/utils/DateUtils';
import { useAutoSelectionTracker } from '../Inputs/AutoSelectionTracker';
import { SellingStrategyComponent } from '../Inputs/SellingStrategy/SellingStrategy';

interface GeneralSectionProps {
    setSectionValidated: (sectionIdx: number, validated: boolean) => void;
    nextButtonVisible: boolean;
    nextButtonDisabled: boolean;
    idx: number;
    maxMediaSelectionCount: number;
    expanded: boolean;
    onChangeSection: (sectionIdx: number) => void;
    useCountryIdToRetrieveMediaChannels: boolean;
    requireSellingStrategy: boolean;
    availableFormats: number[];
    briefToLoad?: BriefJAMDTO;
}

interface GeneralSectionInputedValues {
    startDate: number;
    endDate?: number;
    selectedDays: number[];
    selectedAgency?: Agency;
    selectedAdvertiser?: Advertiser;
    selectedProduct?: Product;
    selectedSector?: Sector;
    selectedTarget?: Target;
    selectedMediaChannels: MediaChannel[];
    selectedCountry?: Country;
    selectedCurrency?: Currency;
    hasBudget: boolean;
    netBudget: number;
    fullBudget: number;
    sellingStrategyType?: SellingStrategyOption;
    sellingStrategyValue?: number;
    discountRate?: number;
    selectedFormats: number[];
}

let tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);

export class GeneralSection extends Section<GeneralSectionProps> {
    Name = "txt_general_criteria";

    SectionBody = (props: GeneralSectionProps) => {
        const { t } = useTranslation();
        const dispatch = useAppDispatch();
        const userDetails = useAppSelector(state => state.user.details);
        const retrievedSectors = useAppSelector(state => state.brief.retrievedData[DataKey.Sector]);
        const retrievedMediaChannels = useAppSelector(state => state.brief.retrievedData[DataKey.MediaChannel]);
        const competitionCodes = useAppSelector(state => state.brief.retrievedData[DataKey.Competition]);
        const digitalBudget = useAppSelector(state => state.brief.inputData?.digitalBudget)
        
        // Use our custom hook to track user-selected fields
        // eslint-disable-next-line
        const { markAsUserSelected, isUserSelected } = useAutoSelectionTracker();
        
        const [inputValues, setInputValues] = React.useState<GeneralSectionInputedValues>({
            startDate: tomorrow.getFullYear() * 10000 + (tomorrow.getMonth() + 1) * 100 + tomorrow.getDate(),
            // endDate: tomorrow.getFullYear() * 10000 + (tomorrow.getMonth() + 1) * 100 + tomorrow.getDate() + 1,
            endDate: undefined,
            selectedDays: [ 0, 1, 2, 3, 4, 5, 6 ],
            selectedMediaChannels: [],
            selectedFormats: [],
            hasBudget: false,
            netBudget: 0,
            fullBudget: 0,
            selectedAdvertiser: undefined,
            selectedAgency: undefined,
            selectedCountry: undefined,
            selectedCurrency: undefined,
            selectedSector: undefined,
            sellingStrategyType: undefined,
            sellingStrategyValue: undefined
        });
        const [isDirectAdvertiser, setIsDirectAdvertiser] = React.useState<boolean>(false);
        const [overrideDiscountRate, setOverrideDiscountRate] = React.useState<boolean>(false);

        // Updated to track user selections
        const setValues = (value: any | any[], key: string | string[], isUserSelection: boolean = true) => {
            let valueArray: any[] = []
            let keyArray: string[] = [] 
            let newInputValues: {[key: string]: any} = {}

            if (key instanceof Array) {
                valueArray = value
                keyArray = key 
            } else {
                valueArray = [value]
                keyArray = [key] 
            }

            if (valueArray.length !== keyArray.length) return

            keyArray.forEach((key, index) => {
                if (isUserSelection) {
                    markAsUserSelected(key);
                }

                newInputValues[key] = valueArray[index]
                dispatch(setInputValue({ key, value: valueArray[index] }));
            })
            setInputValues({ ...inputValues, ...newInputValues });
        }

        React.useEffect(() => {
            if (props.briefToLoad) {
                console.log("generalsection brief to load", props.briefToLoad)
            }
        }, [props.briefToLoad])

        React.useEffect(() => {
            // Validate data
            if (userDetails) {
                const valid = (isSellingStrategyValid() &&
                    inputValues.selectedCurrency !== undefined &&
                    inputValues.selectedCountry !== undefined &&
                    inputValues.selectedMediaChannels.length > 0 &&
                    inputValues.selectedAdvertiser !== undefined &&
                    (inputValues.selectedAdvertiser !== undefined || (userDetails.agencies.length === 0 && userDetails.advertisers.length > 0)) &&
                    inputValues.selectedProduct !== undefined &&
                    inputValues.selectedSector !== undefined &&
                    inputValues.selectedTarget !== undefined &&
                    inputValues.selectedFormats.length > 0 
                )

                props.setSectionValidated(props.idx, valid);
            }
        }, [inputValues, userDetails])

        React.useEffect(() => {
            if (userDetails?.agencies.length === 0 && userDetails?.advertisers.length > 0) {
                setIsDirectAdvertiser(true);
            }
        }, [userDetails]);

        React.useEffect(() => {
            (async () => {
                const selectedAdvertiser = inputValues.selectedAdvertiser;
                if (inputValues.startDate) {
                    let valuesToUpdate: any = {}

                    // Only update selling strategy if it wasn't user-selected
                    if (!overrideDiscountRate) {
                        try {
                            const response = await ApiClientManager.getInstance().getApiClient<ExternalSystemElementApiClient>(ExternalSystemElementApiClient.key)
                                .RetrieveSingleDataFromExternalSource<DiscountRate>(
                                    DataKey.DiscountRate, 
                                    localStorage.getItem('token') || "", 
                                    { selectedAdvertiser: selectedAdvertiser, startDate: inputValues.startDate }
                                );

                            if (response.value) {
                                valuesToUpdate.discountRate = response.value;
                                valuesToUpdate.defaultDiscount = response.value;
                                dispatch(setInputValue({ key: "discountRate", value: response.value }));
                                dispatch(setInputValue({ key: "defaultDiscount", value: response.value }));
                            }
                        }
                        catch (error) {
                            console.error(error);
                            valuesToUpdate.discountRate = 0;
                            valuesToUpdate.defaultDiscount = 0;
                            dispatch(setInputValue({ key: "discountRate", value: 0 }));
                            dispatch(setInputValue({ key: "defaultDiscount", value: 0 }));
                        }

                        if (Object.keys(valuesToUpdate).length > 0) {
                            setInputValues(prev => ({ ...prev, ...valuesToUpdate }));
                        }
                    }
                }
                else if (!selectedAdvertiser) {
                    setValues(undefined, "selectedProduct", false);
                }
            })();
        }, [inputValues.selectedAdvertiser, retrievedSectors, inputValues.startDate]);

        React.useEffect(() => {
            if (inputValues.selectedProduct && retrievedSectors && retrievedSectors.length > 0) {
                const product = inputValues.selectedProduct;

                if (product.mainSectorId && product.mainSectorId !== "-1" && product.mainSectorId !== "") {
                    const sector = retrievedSectors.find(sector => sector.id === product.mainSectorId);

                    if (sector)
                        setValues(sector, "selectedSector", false);
                }
                else if (inputValues.selectedAdvertiser && inputValues.selectedAdvertiser.mainSectorId && 
                         inputValues.selectedAdvertiser.mainSectorId !== "-1" && inputValues.selectedAdvertiser.mainSectorId !== "") {
                    // If product doesn't have a sector, use the advertiser's sector
                    const advertiser = inputValues.selectedAdvertiser;

                    if (advertiser.mainSectorId && advertiser.mainSectorId !== "-1" && advertiser.mainSectorId !== "") {
                        const sector = retrievedSectors.find(sector => sector.id === advertiser.mainSectorId);

                        if (sector)
                            setValues(sector, "selectedSector", false);
                    }
                }
            }
        }, [inputValues.selectedProduct]);

        React.useEffect(() => {
            if (inputValues.selectedSector && retrievedMediaChannels && retrievedMediaChannels.length > 0) {
                const retrievedMediaChannelIds = retrievedMediaChannels.map(mediaChannel => mediaChannel.id);

                dispatch(RetrieveDataFromExternalSource({ dataKey: DataKey.Competition, body: { retrievedMediaChannelIds, selectedSectorIds: [ inputValues.selectedSector.id ] }}));
            }
        }, [retrievedMediaChannels, inputValues.selectedSector]);

        React.useEffect(() => {
            // if (inputValues.selectedAgency !== )
                setValues(undefined, "selectedAdvertiser");
        }, [inputValues.selectedAgency])

        React.useEffect(() => {
            // if (inputValues.selectedAdvertiser !==)
                setValues(undefined, "selectedProduct");
        }, [inputValues.selectedAdvertiser])

        React.useEffect(() => {
            // Remove digital budget from full budget, if there is both
            setValues(inputValues.hasBudget && digitalBudget ? Math.max(inputValues.fullBudget - digitalBudget, 0) : inputValues.fullBudget, "netBudget")
        }, [inputValues.fullBudget, inputValues.hasBudget, digitalBudget])

        React.useEffect(() => {
            dispatch(setInputValue({ key: "competitionCodes", value: competitionCodes }));
        }, [competitionCodes]);

        const onFormSubmit = (event: any) => {
            event.preventDefault();
            setValues(inputValues.startDate, "startDate");
            setValues(inputValues.endDate, "endDate");
            props.onChangeSection(props.idx + 1);
        }

        const onDirectAdvertiserClick = () => {
            setIsDirectAdvertiser(!isDirectAdvertiser);
        }

        // When the user clicks the "custom rate" checkbox, mark the selling strategy as user-selected
        const onOverrideDiscountRateClick = () => {
            setOverrideDiscountRate(!overrideDiscountRate);
            if (!overrideDiscountRate) {
                markAsUserSelected("discountRate");
            }
        }

        const isSellingStrategyValid = (): boolean => {
            // if fields are not displayed, return OK
            if (userDetails?.external) return true
            if (!props.requireSellingStrategy) return true

            // fields must have value
            if (inputValues.sellingStrategyValue === undefined) return false
            // discount between 0 and 100%
            if (inputValues.sellingStrategyType?.id === "discount" && !(inputValues.discountRate && inputValues.discountRate >= 0 && inputValues.discountRate < 100))
                return false
            // // if not spotcount, budget must be > 0
            // if (inputValues.sellingStrategyType?.id !== "spotcount" && !(inputValues.netBudget && inputValues.netBudget > 0))
            //     return false
            // budget/spot count must be > 0
            if (!(inputValues.fullBudget && inputValues.fullBudget > 0))
                return false
            // if other than discount or spotcount, value must be > 0
            if (
                    inputValues.sellingStrategyType?.id !== "discount"
                    && inputValues.sellingStrategyType?.id !== "spotcount"
                    && !(inputValues.sellingStrategyValue && inputValues.sellingStrategyValue > 0)
                )
                return false


            return true
        }

        const setDates = (startDate: number, endDate: number) => {
            setInputValues({ ...inputValues, startDate: startDate, endDate: endDate });
            dispatch(setInputValue({ key: "startDate", value: startDate }));
            dispatch(setInputValue({ key: "endDate", value: endDate }));
        }

        return (
            <Form onSubmit={onFormSubmit}>
                <Row className="briefInputRow">
                    <Col className="multipleInputsRow">
                        <Input required={userDetails?.qualified && !isDirectAdvertiser} title={t("txt_agency")}>
                            <AgencyPicker
                                allowNew={!userDetails?.qualified}
                                setSelectedAgency={(agency) => setValues(agency, "selectedAgency")}
                                value={inputValues.selectedAgency}
                                filter={userDetails?.agencies}
                                disabled={ isDirectAdvertiser || (userDetails?.agencies && userDetails.agencies.length === 0 && userDetails?.advertisers && userDetails.advertisers.length > 0)}
                            />
                        </Input>
                    </Col>
                    <Col className="multipleInputsRow">
                        <Input
                            required
                            title={t("annoncer")}
                            checkbox={{
                                display: true,
                                title: t("txt_direct_advertiser"),
                                id: 'directAdvertiserCheck',
                                checked: isDirectAdvertiser,
                                onChange: onDirectAdvertiserClick,
                                className: "directAdvertiserCheck"
                            }}
                        >
                            <AdvertiserPicker
                                allowNew={!userDetails?.qualified}
                                // disabled if 
                                // user has no agencies and advertisers and no agency is selected
                                // user has no agencies and no agency is selected
                                // should not be disabled if user has advertisers and no agency
                                disabled={
                                    userDetails && (
                                        (!isDirectAdvertiser && userDetails.agencies.length === 0 && userDetails.advertisers.length === 0 && !inputValues.selectedAgency) ||
                                        (userDetails.agencies.length > 0 && !inputValues.selectedAgency)
                                    )
                                }
                                setSelectedAdvertiser={(advertiser) => setValues(advertiser, "selectedAdvertiser")}
                                value={inputValues.selectedAdvertiser}
                                filter={userDetails?.advertisers}
                                filterData={{agencyIds: (inputValues.selectedAgency && inputValues.selectedAgency.id) ? inputValues.selectedAgency.id : "-1"}}
                            />
                        </Input>
                    </Col>
                </Row>
                <Row className="briefInputRow">
                    <Col className="multipleInputsRow">
                        <Input required title={t("product")}>
                            <ProductPicker
                                allowNew={!userDetails?.qualified}
                                disabled={!inputValues.selectedAdvertiser}
                                setSelectedProduct={(product) => {
                                    setValues(product, "selectedProduct");
                                }}
                                filterData={inputValues.selectedAdvertiser}
                                value={inputValues.selectedProduct}
                            />
                        </Input>
                    </Col>
                    <Col className="multipleInputsRow">
                        <Input required title={t("txt_sector")}>
                            <SectorPicker
                                setSelectedSector={(sector) => setValues(sector, "selectedSector")}
                                value={inputValues.selectedSector}
                            />
                        </Input>
                    </Col>
                </Row>
                <Row className="briefInputRow">
                    {props.useCountryIdToRetrieveMediaChannels &&
                        <Col className="multipleInputsRow">
                            <Input required title={t("txt_country_label")}>
                                <CountryPicker
                                    setSelectedCountry={(country) => setValues(country, "selectedCountry")}
                                    value={inputValues.selectedCountry}
                                />
                            </Input>
                        </Col>
                    }
                    <Col className="multipleInputsRow">
                        <Input required title={t("supports")}>
                            <MediaChannelPicker
                                filterData={inputValues.selectedCountry}
                                disabled={props.useCountryIdToRetrieveMediaChannels && !inputValues.selectedCountry}
                                setSelectedMediaChannels={(mediaChannels) => setValues(mediaChannels, "selectedMediaChannels")}
                                value={inputValues.selectedMediaChannels}
                            />
                        </Input>
                    </Col>
                </Row>
                <Row className="briefInputRow">
                    <Col className="multipleInputsRow">
                        <Input required title={t("txt_target")}>
                            <TargetPicker
                                setSelectedTarget={(target) => setValues(target, "selectedTarget")}
                                value={inputValues.selectedTarget}
                            />
                        </Input>
                    </Col>
                    <Col className="multipleInputsRow">
                        <Input required title={t("txt_format")}>
                            <FormatPicker
                                required
                                setInputValue={setValues}
                                availableFormats={props.availableFormats}
                                values={inputValues.selectedFormats}
                            />
                        </Input>
                    </Col>
                </Row>
                <Row className="briefInputRow">
                    <Input required title={t("txt_broadcast_period")}>
                        <PeriodPicker
                            startDate={inputValues.startDate}
                            endDate={inputValues.endDate}
                            setDates={setDates}
                            setStartDate={(date) => setValues(date, "startDate")}
                            setEndDate={(date) => setValues(date, "endDate")}
                            minDate={dateObjectToNumber(tomorrow)}
                        />
                    </Input>
                </Row>
                <Row className="briefInputRow">
                    <Input title={t("txt_broadcast_days")}>
                        <DaysPicker
                            selectedDays={inputValues.selectedDays}
                            setSelectedDays={(days) => setValues(days, "selectedDays")} />
                    </Input>
                </Row>
                {userDetails && <SellingStrategyComponent
                    setValues={setValues}
                    inputValues={inputValues}
                    overrideDiscountRate={overrideDiscountRate}
                    onOverrideDiscountRateClick={onOverrideDiscountRateClick}
                    externalUser={userDetails.external as boolean}
                />}
                {
                    props.nextButtonVisible &&
                    <Col className="text-center">
                        <Button
                            type="submit"
                            className="sectionContinueButton"
                            variant="dark"
                            disabled={props.nextButtonDisabled}>
                            {t("next_button")}
                        </Button>
                    </Col>
                }
            </Form>
        );
    }
}